import React from "react"
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from "recoil"
import { PendingSpinner } from "../Pending"
import { quadriProjectIdState, sidebarWorksetState, userClaimsState, userProjectRoleState } from "../StateSelectors"
import DisconnectModal from "./DisconnectModal"
import { formatDateAsConnect } from "../../util/dateTools"
import { ErrorBoundary, FallbackProps } from "react-error-boundary"
import Oops from "../Oops/Oops"
import { useTranslation } from "react-i18next"
import { TASKS_CHUNK_SIZE, TasksDisplaySidebar, scrollElementId } from "../TasksDisplaySidebar"
import { GET_WORKSET_TASKS } from "../../api/QuadriApi"

const WorksetsSidebar = () => {
  const quadriProjectId = useRecoilValue(quadriProjectIdState)
  const userClaims = useRecoilValue(userClaimsState)
  const [sidebar, setSidebar] = useRecoilState(sidebarWorksetState)
  const userProjectRole = useRecoilValueLoadable(userProjectRoleState)
  const [tabActive, setTabActive] = React.useState(0)
  const [showDisconnectModal, setShowDisconnectModal] = React.useState(false)

  const showModal = () => setShowDisconnectModal(true)
  const closeModal = () => setShowDisconnectModal(false)
  const { t } = useTranslation(["worksets", "common"])

  const reservedTaskIds = sidebar?.reservedTaskIds || []
  const reservedTaskIdsLength = reservedTaskIds.length
  const getTaskIdsSlice = (startIdx: number) =>
    reservedTaskIds.slice(startIdx, Math.min(startIdx + TASKS_CHUNK_SIZE, reservedTaskIds.length))

  if (sidebar && userProjectRole.contents && quadriProjectId) {
    const buttonDisconnectWorkset = showModal

    let activity = [...sidebar.activity!] || []
    if (activity.length > 0) {
      activity.sort((a, b) => new Date(b.time).valueOf() - new Date(a.time).valueOf())
    }

    // Check if workset userId is the same as the signed in user id
    const userDoesNotOwnWorkset = sidebar.userId !== userClaims.sub
    const disableDisconnectButton = userProjectRole.contents !== "ADMIN" && userDoesNotOwnWorkset

    return (
      <div className="panel">
        {showDisconnectModal && (
          <DisconnectModal
            closeModal={closeModal}
            worksetName={sidebar.name}
            worksetId={sidebar.id}
            quadriProjectId={quadriProjectId}
            user={sidebar.fullName}
          />
        )}
        <header>
          <div className="panel-title">
            <h3>{sidebar.name}</h3>
          </div>
          <button className="button icon-circle icon-medium tertiary" onClick={() => setSidebar(null)}>
            <i className="icon-font tc-icon-content-close" />
          </button>
        </header>

        <section id={scrollElementId}>
          <div className="panel-body">
            <div className="sub-section border-bottom">
              <div className="section-details pb-1">
                <div className="label-group">
                  <label>{t("sidebar.created")}</label>
                  <div className="value">
                    {formatDateAsConnect(sidebar.createdTime)} {t("sidebar.by")} {sidebar.fullName}
                  </div>
                </div>
                <div className="label-group">
                  <label>{t("sidebar.lastActivity")}</label>
                  <div className="value">{formatDateAsConnect(activity[0].time) || "none"}</div>
                </div>
                <div className="label-group">
                  <label>{t("sidebar.internalId", { ns: "common" })}</label>
                  <div className="value">{sidebar.id}</div>
                </div>
              </div>
            </div>

            <div className="sub-section">
              <ul className="tabs mb-0">
                <li
                  className={`tabs-title text-ellipsis ${tabActive === 0 && "active"}`}
                  onClick={() => setTabActive(0)}
                >
                  <span className="text-ellipsis px-1">
                    {t("sidebar.reservedTasks")}
                    {" ("}
                    {reservedTaskIdsLength.toString()}
                    {")"}
                  </span>
                </li>
                <li
                  className={`tabs-title text-ellipsis ${tabActive === 2 && "active"}`}
                  onClick={() => setTabActive(2)}
                >
                  <span className="text-ellipsis px-1">{t("sidebar.activity")}</span>
                </li>
              </ul>

              <div className="tabs-content">
                {reservedTaskIds.length > 0 ? (
                  <div className={`tabs-panel ${tabActive === 0 && "active"}`}>
                    <ErrorBoundary FallbackComponent={TasksErrorFallback}>
                      <TasksDisplaySidebar
                        nTasksTotal={reservedTaskIds.length}
                        quadriProjectId={quadriProjectId}
                        scrollElementId={scrollElementId}
                        include_deleted={false}
                        getTaskIdsSlice={getTaskIdsSlice}
                        gqlQuery={GET_WORKSET_TASKS}
                      />
                    </ErrorBoundary>
                  </div>
                ) : (
                  <div className={`tabs-panel ${tabActive === 0 && "active"}`}>
                    <EmptyTasksList />
                  </div>
                )}
                <div className={`tabs-panel ${tabActive === 2 && "active"}`}>
                  <ul>
                    {activity.map((a, i) => (
                      <li className="selectable pt-1 pb-2 pr-1 border-bottom" key={i}>
                        <div className="col">
                          <div className="activity-description">
                            <b>{a.type}</b> &bull; {a.description}
                          </div>
                          <div className="sub-text">{formatDateAsConnect(a.time)}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {sidebar.status === "Connected" && (
          <footer className="px-2 py-2">
            {disableDisconnectButton ? (
              <button className="button icon-left link-warning full-width" disabled={true}>
                {t("sidebar.disconnectWorkset")}
              </button>
            ) : (
              <button className="button icon-left link-warning full-width" onClick={buttonDisconnectWorkset}>
                <i className="icon-font tc-icon-cloud-off"></i>
                {t("sidebar.disconnectWorkset")}
              </button>
            )}
          </footer>
        )}
      </div>
    )
  }

  return (
    <div className="panel">
      <PendingSpinner />
    </div>
  )
}

const TasksErrorFallback = (props: FallbackProps) => {
  const { t } = useTranslation("worksets")

  return (
    <div className="mt-2">
      <Oops body={t("sidebar.tasksLoadError")} />
    </div>
  )
}

const EmptyTasksList = () => {
  const { t } = useTranslation("worksets")
  return (
    <div className="d-flex h-100 flex-column justify-content-center align-items-center text-center text-muted mx-2">
      <h3 className="mb-1 mt-2" style={{ fontWeight: 400 }}>
        {t("sidebar.noReservedTasksHeading")}
      </h3>
      <div>
        <p>{t("sidebar.noReservedTasksMessage")}</p>
      </div>
    </div>
  )
}

export default WorksetsSidebar
